/**
 * SoleadLab 2.0 - Asset Upload Service
 *
 * Uploads binary assets (overlays, orthophotos, photos) to R2 via the API
 * and returns persistent URLs that replace ephemeral blob URLs.
 */

import { apiFetch, apiFetchRaw, API_BASE, getAuthHeaders } from './apiFetch';
// Re-export for legacy callers (e.g., direct asset URL construction).
export { API_BASE, getAuthHeaders };

export interface AssetUploadResult {
  assetId: string;
  r2Key: string;
  url: string; // full URL to retrieve the asset
  originalName: string;
  contentType: string;
  size: number;
}

/**
 * Upload a processed binary asset to R2.
 *
 * @param file      - Blob or File to upload
 * @param siteId    - Site/project ID (determines R2 path)
 * @param category  - Asset category: 'overlay', 'orthophoto', 'photo', 'geo_photo'
 * @param filename  - Original or generated filename (used for extension detection)
 * @returns Full asset URL for persistence in ProjectData
 */
export async function uploadAsset(
  file: Blob,
  siteId: string,
  category: string,
  filename: string
): Promise<AssetUploadResult> {
  const formData = new FormData();
  formData.append('file', file, filename);
  formData.append('siteId', siteId);
  formData.append('category', category);

  // 5 min timeout — orthophoto / overlay images can be tens of MB and the
  // 30s default times out partway through the multipart POST body, leaving
  // the entity row pinned to its blob: URL on the client. Same budget
  // uploadLargeAsset uses for its multipart chunks.
  const data = await apiFetch<{
    assetId: string;
    r2Key: string;
    originalName: string;
    contentType: string;
    size: number;
  }>('/api/v2/assets', { method: 'POST', body: formData }, 5 * 60 * 1000);

  return { ...data, url: `${API_BASE}/api/v2/assets/${data.r2Key}` };
}

/**
 * Upload a canvas element as a PNG to R2.
 */
export async function uploadCanvasAsset(
  canvas: HTMLCanvasElement,
  siteId: string,
  category: string,
  filename: string
): Promise<AssetUploadResult> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
      'image/png'
    );
  });
  return uploadAsset(blob, siteId, category, filename);
}

/**
 * Import a Google Drive video to R2 for direct playback.
 */
export async function importDriveVideo(
  siteId: string,
  driveUrl: string
): Promise<AssetUploadResult> {
  const data = await apiFetch<{
    assetId: string;
    r2Key: string;
    contentType: string;
    size: number;
  }>(`/api/v2/sites/${siteId}/import-video`, {
    method: 'POST',
    body: JSON.stringify({ url: driveUrl }),
  });

  return {
    ...data,
    url: `${API_BASE}/api/v2/assets/${data.r2Key}`,
    originalName: '',
  };
}

// ===================================================================
// Multipart Upload (for large files > 100MB)
// ===================================================================

// 50MB threshold — files over this go through multipart so each request is
// smaller, more recoverable on flaky links, and stays well under the Worker
// 100MB body cap. Below this, the single-shot path is fine.
const MULTIPART_THRESHOLD = 50 * 1024 * 1024;
const CHUNK_SIZE = 25 * 1024 * 1024; // 25MB per chunk — keep each PUT well under any proxy/connection time-out budget

/**
 * Upload a large file via multipart upload (chunks through the Worker).
 * Falls back to standard uploadAsset for files under MULTIPART_THRESHOLD.
 */
export async function uploadLargeAsset(
  file: File | Blob,
  siteId: string,
  category: string,
  filename: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<AssetUploadResult> {
  if (file.size <= MULTIPART_THRESHOLD) {
    return uploadAsset(file, siteId, category, filename);
  }

  const contentType = (file as File).type || 'application/octet-stream';

  // Step 1: Create multipart upload
  const { uploadId, r2Key, assetId } = await apiFetch<{
    uploadId: string;
    r2Key: string;
    assetId: string;
  }>('/api/v2/upload/multipart/create', {
    method: 'POST',
    body: JSON.stringify({ siteId, category, filename, contentType }),
  });

  // Step 2: Upload chunks
  //
  // JWT-expiry note (observed once as a mid-upload `multipart/part 401` on a
  // multi-GB upload): each part goes through apiFetch, which already does the
  // 401 → silent-refresh → retry-once dance and re-sends the same Blob slice,
  // and a proactive 13-min timer refreshes ahead of the 15-min token expiry.
  // The residual risk on hours-long uploads is a part whose refresh+retry BOTH
  // fail (e.g. network blip exactly at rotation) — that aborts the whole
  // multipart (catch below). If that starts biting real users, add per-part
  // retry-with-backoff here rather than widening token lifetime.
  const totalSize = file.size;
  const totalParts = Math.ceil(totalSize / CHUNK_SIZE);
  const parts: Array<{ partNumber: number; etag: string }> = [];
  let uploaded = 0;

  try {
    for (let i = 0; i < totalParts; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, totalSize);
      const chunk = file.slice(start, end);
      const partNumber = i + 1;

      const params = new URLSearchParams({
        key: r2Key,
        uploadId,
        partNumber: String(partNumber),
      });

      // Raw chunk PUT — pass the Blob as body. apiFetch leaves it alone
      // (only FormData triggers the Content-Type strip), but a raw Blob with
      // an explicit Content-Type works fine.
      const partData = await apiFetch<{ etag: string; partNumber: number }>(
        `/api/v2/upload/multipart/part?${params}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: chunk,
        },
        // Per-chunk timeout: 5 min for 50MB upload over slow connections.
        5 * 60 * 1000,
      );
      parts.push({ partNumber, etag: partData.etag });

      uploaded += (end - start);
      onProgress?.(uploaded, totalSize);
    }

    // Step 3: Complete multipart upload
    await apiFetch<unknown>('/api/v2/upload/multipart/complete', {
      method: 'POST',
      body: JSON.stringify({ key: r2Key, uploadId, parts }),
    });

    return {
      assetId,
      r2Key,
      url: `${API_BASE}/api/v2/assets/${r2Key}`,
      originalName: filename,
      contentType,
      size: totalSize,
    };
  } catch (err) {
    // Best-effort abort of the multipart upload on failure.
    apiFetch('/api/v2/upload/multipart/abort', {
      method: 'POST',
      body: JSON.stringify({ key: r2Key, uploadId }),
    }).catch(() => {});
    throw err;
  }
}

// ===================================================================
// Google Drive Folder Import
// ===================================================================

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size: string;
  /** Subfolder path within the scanned root (empty string = root). e.g. "א/" or "א/sub/" */
  path: string;
}

export interface DriveFileImportResult {
  assetId: string;
  r2Key: string;
  url: string;
  contentType: string;
  size: number;
  exif?: {
    lat: number;
    lng: number;
    altitude?: number;
    bearing?: number;
    captureDate?: string;
  };
}

export interface DriveSrtResult {
  content: string;
  fileName: string;
}

/** EXIF-only peek result: the file stays on Google Drive, nothing in R2. */
export interface DriveExifResult {
  exif?: DriveFileImportResult['exif'];
  fileName: string;
}

/**
 * List files in a publicly shared Google Drive folder.
 */
export async function listDriveFolder(
  siteId: string,
  folderUrl: string
): Promise<{
  folderId: string;
  fileCount: number;
  truncated: boolean;
  truncationReason?: 'files' | 'depth' | 'folders';
  files: DriveFile[];
}> {
  return apiFetch(
    `/api/v2/sites/${siteId}/import-drive-folder`,
    {
      method: 'POST',
      body: JSON.stringify({ folderUrl }),
    },
  );
}

/**
 * Import a single file from Google Drive to R2.
 * For SRT files, returns text content instead of R2 URL.
 */
export async function importDriveFile(
  siteId: string,
  fileId: string,
  fileName: string,
  category: 'photo' | 'orthophoto' | 'overlay' | 'sld' | 'srt'
): Promise<DriveFileImportResult | DriveSrtResult> {
  const data = await apiFetch<unknown>(
    `/api/v2/sites/${siteId}/import-drive-file`,
    {
      method: 'POST',
      body: JSON.stringify({ fileId, fileName, category }),
    },
  );

  if (category === 'srt') {
    return data as DriveSrtResult;
  }

  const d = data as {
    assetId: string;
    r2Key: string;
    contentType: string;
    size: number;
    exif?: DriveFileImportResult['exif'];
  };
  return {
    ...d,
    url: `${API_BASE}/api/v2/assets/${d.r2Key}`,
  } as DriveFileImportResult;
}

/**
 * Extract EXIF GPS from a Drive photo WITHOUT importing it — the backend reads
 * only the first 128KB of the file and stores nothing in R2. Used for the
 * visual/zoom anchor of thermal-less photo sets, which stay hosted on Drive.
 */
export async function peekDriveFileExif(
  siteId: string,
  fileId: string,
  fileName: string
): Promise<DriveExifResult> {
  return apiFetch<DriveExifResult>(
    `/api/v2/sites/${siteId}/import-drive-file`,
    {
      method: 'POST',
      body: JSON.stringify({ fileId, fileName, category: 'photo', exifOnly: true }),
    },
  );
}

// ===================================================================
// R2 Asset Deletion
// ===================================================================

/**
 * Delete an R2 asset by its full URL **or by a bare R2 key**.
 *
 * Bare keys are accepted because callers legitimately hold them: entity rows
 * store `sourceR2Key` (the original upload) alongside a display `url`, and
 * both removeOrthophoto and removeOverlay pass that key straight here. Until
 * this accepted them, those calls matched no marker and returned false BEFORE
 * issuing any request — silently, since scheduleAssetDeletion discards the
 * result. Every orthophoto source file (frequently gigabytes) and every plan
 * PDF has been left behind in R2 as a result.
 *
 * Anything that is neither an asset URL nor a site-scoped key is still
 * rejected, so external URLs (Google Drive, signed third-party links) can
 * never be turned into a delete against our own bucket.
 *
 * Returns true if deleted (or already gone), false if it is not ours to delete.
 */
export async function deleteAsset(assetUrl: string): Promise<boolean> {
  const marker = '/api/v2/assets/';
  const idx = assetUrl.indexOf(marker);

  let r2Key: string;
  if (idx !== -1) {
    r2Key = assetUrl.slice(idx + marker.length);
  } else if (/^sites\/[^/]+\/.+/.test(assetUrl)) {
    // Bare key, e.g. sites/<siteId>/overlay/<uuid>.pdf — every upload lands
    // under this prefix (uploads.ts), so it is the safe shape to accept.
    r2Key = assetUrl;
  } else {
    return false;
  }
  if (!r2Key) return false;

  const res = await apiFetchRaw(`/api/v2/assets/${r2Key}`, { method: 'DELETE' });
  if (res.status === 404) return true; // Already gone
  if (!res.ok) {
    console.warn(`[deleteAsset] Failed to delete ${r2Key}: ${res.status}`);
    return false;
  }
  return true;
}

/**
 * Fire-and-forget R2 deletion for one or more asset URLs.
 * Non-R2 URLs (Google Drive, etc.) are silently skipped.
 * Failures are logged but never thrown.
 */
export function scheduleAssetDeletion(...urls: (string | undefined | null)[]): void {
  for (const url of urls) {
    if (!url) continue;
    deleteAsset(url).catch((err) => {
      console.warn('[scheduleAssetDeletion] Error:', err);
    });
  }
}

export function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'application/octet-stream';
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mime });
}
